import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CentralServerService } from 'services/central-server.service';
import { DialogService } from 'services/dialog.service';
import { MessageService } from 'services/message.service';
import { SpinnerService } from 'services/spinner.service';
import { WindowService } from 'services/window.service';
import { ChargingStation, Connector, OCPPGeneralResponse, QrCodeData } from 'types/ChargingStation';
import { ActionResponse, Paging } from 'types/DataResult';
import { ButtonAction, FilterParams } from 'types/GlobalType';
import { Transaction } from 'types/Transaction';
import { User } from 'types/User';
import { Utils } from 'utils/Utils';
import { SessionService } from '../service/session.service';

@Component({
  selector: 'app-charging',
  templateUrl: 'charging.component.html',
  styleUrls: ['charging.component.scss']
})
export class ChargingComponent implements OnInit, OnDestroy {
  tagId: string;
  userId: string;
  transactionId: number | null = null;
  transactionDetails: Transaction;
  formattedDuration: string;
  energyInKWh: number;
  intervalId: any;
  isTransactionStopped = false;
  chargingStationId: string;
  connectorId: number;
  connector: Connector;
  codedChargingStationID: string;
  codedConnectorID: string;
  QrCodeData: QrCodeData | null = null;
  chargingStation: ChargingStation;
  public connectorStatus: string;
  public connectorExists = true;
  invoiceUrl: string;
  public isLoadingInvoice = false;
  transactoinStarted = false;
  user: User;
  public loading = false;
  isLoading: boolean;
  paymentStatus: any;
  qrCodeData: any;
  selectedLanguage = 'fr';
  tenantName = '';
  tenantLogo = '';
  private token: string;
  private authToken: string;
  private sessionToken: string;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private centralServerService: CentralServerService,
    private spinnerService: SpinnerService,
    private translateService: TranslateService,
    private messageService: MessageService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private sessionService: SessionService,
    private windowService: WindowService
  ) { }

  public async ngOnInit(): Promise<void> {
    this.sessionToken = this.route.snapshot.paramMap.get('sessionToken');
    const language = this.translateService.currentLang || 'en';
    const supportedLanguages = ['en', 'fr'];
    const languageToUse = supportedLanguages.includes(language) ? language : 'en';
    this.selectedLanguage = languageToUse;
    this.translateService.use(languageToUse);
    if (this.sessionToken) {
      await this.loadSessionFromEmail();
      return;
    }
    this.route.paramMap.subscribe((params) => {
      this.codedChargingStationID = params.get('codedChargingStationID');
      this.codedConnectorID = params.get('codedConnectorID');
    });
    const state = history.state;
    this.qrCodeData = state?.qrCodeData || null;
    this.paymentStatus = state?.paymentStatus || null;

    if (!this.qrCodeData || !this.paymentStatus?.isPaymentCompleted) {
      // Redirect to payment if state is missing or payment not completed
      void this.router.navigate([`/qrcode/${this.route.snapshot.params.codedChargingStationID}/${this.route.snapshot.params.codedConnectorID}/payment`]);
    }

    this.userId = this.sessionService.getUserId() || '';
    this.tagId = this.sessionService.getTagId() || '';

    if (this.codedChargingStationID && this.codedConnectorID) {
      try {
        this.QrCodeData = await firstValueFrom(this.centralServerService.getQrCodeData(this.codedChargingStationID, this.codedConnectorID));
        if (this.QrCodeData) {
          this.chargingStationId = this.QrCodeData.chargingStationID;
          this.connectorId = Number(this.QrCodeData.connectorID);
          this.token = this.QrCodeData.token;
        } else {
          this.messageService.showErrorMessage('Failed to load QR code data');
        }
      } catch (error) {
        console.error('Error fetching QR code data:', error);
        this.messageService.showErrorMessage('Error loading payment data');
      }
    } else {
      console.error('codedChargingStationID or codedConnectorID is missing');
      this.messageService.showErrorMessage('Invalid charging station or connector ID');
    }

    this.loadTenantBrand();

    this.chargingStation = await firstValueFrom(this.centralServerService.getChargingStation(this.chargingStationId));
    this.connector = Utils.getConnectorFromID(this.chargingStation, this.connectorId);

    this.intervalId = setInterval(async () => {
      if (this.transactionId) {
        console.log('ChargingComponent ~ this.intervalId=setInterval ~ this.transactionId:', this.transactionId);
        this.isLoading = true;
        try {
          await this.loadChargingStation(this.chargingStationId, this.connectorId);
          this.getTransactionDetails();
        } catch (error) {
          console.error('Error during interval loading:', error);
        } finally {
          this.isLoading = false;
        }
      }
    }, 10000);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  redirectToEv24(): void {
    const params = new URLSearchParams({
      tenantName: this.tenantName || '',
      chargingStationName: this.chargingStationId || '',
      connectorId: this.connectorId?.toString() || '',
    });
    window.location.href = `https://qr.ev24.support/?${params.toString()}`;
  }

  redirectToWattzhub(): void {
    window.location.href = 'https://www.wattzhub.com';
  }

  public changeLanguage(language: string): void {
    this.selectedLanguage = language;
    this.translateService.use(language);
  }

  public stopTransaction(dialogService: DialogService, translateService: TranslateService): void {
    dialogService.createAndShowYesNoDialog(
      translateService.instant('stop_transaction_title'),
      translateService.instant('stop_transaction_confirm', { chargeBoxID: this.chargingStation.id, transactionId: this.transactionId })
    ).subscribe((response) => {
      if (response === ButtonAction.YES) {
        this.spinnerService.show();
        const stopTransaction = this.sessionToken
          ? this.centralServerService.stopTransactionQrSession(this.sessionToken)
          : this.centralServerService.stopTransaction(this.transactionId);
        stopTransaction.subscribe({
          next: (res: ActionResponse) => {
            this.spinnerService.hide();
            if (res.status === OCPPGeneralResponse.ACCEPTED) {
              console.log(`Transaction ${this.transactionId} stopped successfully.`);
              if (this.connectorExists) {
                this.isTransactionStopped = true;
                this.messageService.showInfoMessage(
                  translateService.instant('chargers.transaction_stopped'),
                  translateService.instant('chargers.please_unplug_connector')
                );
                if (this.sessionToken) {
                  this.isLoadingInvoice = true;
                  this.pollForSessionInvoice();
                } else {
                  this.promptForInvoice();
                }
              }
              if (this.intervalId) {
                clearInterval(this.intervalId);
              }
            } else {
              console.error('Failed to stop transaction');
            }
          },
          error: (error) => {
            this.spinnerService.hide();
            console.error('Error stopping transaction:', error);
          }
        });
      }
    });
  }

  public async getUserTransactions(): Promise<number | null> {
    try {
      const filterParams: FilterParams = {
        UserID: this.userId, Issuer: 'true', WithCompany: 'true', WithSite: 'true', WithSiteArea: 'true',
        WithTag: 'true', WithUser: 'true', WithCar: 'true', WithChargingStation: 'true', Statistics: 'ongoing',
        Limit: '50', SortFields: '-timestamp'
      };
      console.log('ChargingComponent ~ getUserTransactions ~ userId:', this.userId);
      const transactionResult = await firstValueFrom(this.centralServerService.getActiveTransactions(filterParams));
      if (transactionResult.result.length > 0) {
        console.log('Transaction ID retrieved:', transactionResult.result[0].id);
        return transactionResult.result[0].id;
      }
      console.log('No active transactions found for user');
      return null;
    } catch (err) {
      console.error('Error retrieving transactions:', err);
      return null;
    }
  }

  public getTransactionDetails(): void {
    if (this.sessionToken) {
      this.centralServerService.getTransactionQrSession(this.sessionToken).subscribe({
        next: (transaction) => this.setSessionTransactionDetails(transaction),
        error: () => this.messageService.showErrorMessage('This QR code session link is invalid'),
      });
      return;
    }
    this.centralServerService.getTransaction(this.transactionId).subscribe({
      next: (transaction) => {
        this.transactionDetails = transaction;
        console.log('ChargingComponent ~ this.centralServerService.getTransaction ~ this.transactionDetails:', this.transactionDetails);
        this.formattedDuration = Utils.convertSecondsToMinutesAndSeconds(transaction.currentTotalDurationSecs);
        this.energyInKWh = Utils.convertWattsToKWh(transaction.currentTotalConsumptionWh);
      },
      error: (error) => {
        console.error('Error fetching transaction details:', error);
      }
    });
  }

  private async loadSessionFromEmail(): Promise<void> {
    try {
      const transaction = await firstValueFrom(this.centralServerService.getTransactionQrSession(this.sessionToken));
      this.setSessionTransactionDetails(transaction);
      this.userId = this.sessionService.getUserId() || transaction.user?.id || '';
      this.tagId = this.sessionService.getTagId() || '';
      this.chargingStationId = transaction.chargeBoxID;
      this.connectorId = transaction.connectorId;
      this.loadTenantBrand();
      this.chargingStation = await firstValueFrom(this.centralServerService.getChargingStationQr(this.chargingStationId));
      this.connector = Utils.getConnectorFromID(this.chargingStation, this.connectorId);
      this.connectorExists = !!this.connector;
      this.connectorStatus = this.connector?.status || '';
      if (this.isTransactionStopped && !this.invoiceUrl) {
        this.isLoadingInvoice = true;
        this.pollForSessionInvoice();
      } else if (!this.isTransactionStopped) {
        this.intervalId = setInterval(() => this.getTransactionDetails(), 10000);
      }
    } catch (error) {
      console.error('Error loading QR code session:', error);
      this.messageService.showErrorMessage('This QR code session link is invalid');
    }
  }

  private setSessionTransactionDetails(transaction: Transaction): void {
    this.transactionId = transaction.id;
    this.transactionDetails = transaction;
    this.isTransactionStopped = !!transaction.stop;
    if (transaction.invoiceDownloadUrl) {
      this.invoiceUrl = transaction.invoiceDownloadUrl;
      this.isLoadingInvoice = false;
    }
    this.formattedDuration = Utils.convertSecondsToMinutesAndSeconds(
      transaction.stop?.totalDurationSecs ?? transaction.currentTotalDurationSecs
    );
    this.energyInKWh = Utils.convertWattsToKWh(
      transaction.stop?.totalConsumptionWh ?? transaction.currentTotalConsumptionWh
    );
    if (this.isTransactionStopped && this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  public async startTransaction(): Promise<void> {
    try {
      console.log('Starting transaction...');
      this.spinnerService.show();
      const response = await this.centralServerService.startTransaction(this.chargingStationId, this.connectorId, this.userId, this.tagId).toPromise();
      if (response.status === OCPPGeneralResponse.ACCEPTED) {
        console.log('Transaction started successfully');
        await new Promise(resolve => setTimeout(resolve, 3000));
        let retryCount = 0;
        const maxRetries = 15;
        while (retryCount < maxRetries) {
          this.transactionId = await this.getUserTransactions();
          if (this.transactionId) {
            break;
          }
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        if (this.transactionId) {
          this.getTransactionDetails();
        } else {
          console.error('Transaction ID is null or undefined after retries');
        }
      } else {
        console.error('Failed to start transaction');
      }
    } catch (error) {
      console.error('Error starting transaction:', error);
    } finally {
      this.spinnerService.hide();
    }
  }

  public ViewInvoice(): void {
    if (this.invoiceUrl) {
      window.open(this.invoiceUrl, '_blank');
    } else {
      console.log('Invoice URL not found');
    }
  }

  public async getInvoiceByTransactionID(userID: string, transactionID: number): Promise<boolean> {
    return new Promise((resolve) => {
      this.spinnerService.show();
      this.isLoadingInvoice = true;
      const paging: Paging = { limit: 1000, skip: 0 };
      this.centralServerService.getInvoices({ userID }, paging).subscribe({
        next: (invoices) => {
          if (invoices && invoices.result.length > 0) {
            const foundInvoice = invoices.result.find(invoice => invoice.sessions.some(s => s.transactionID === transactionID));
            if (foundInvoice) {
              this.sendInvoiceToUser(foundInvoice);
              resolve(true);
            }
          }
          console.log('No invoice found for this transaction ID.');
          resolve(false);
        },
        error: (error) => {
          console.error('Error fetching invoice:', error);
          resolve(false);
        }
      });
      this.spinnerService.hide();
    });
  }

  public logout() {
    this.sessionService.clearUserId();
    this.sessionService.clearTagId();
    this.sessionService.clearLoggedUser();
    this.centralServerService.logout().subscribe({
      next: () => {
        this.centralServerService.clearLoginInformation();
      },
      error: (error) => {
        this.centralServerService.clearLoginInformation();
      }
    });
  }

  private loadTenantBrand(): void {
    this.tenantName = this.QrCodeData?.tenantName || '';
    const subdomain = this.windowService.getSubdomain();
    if (subdomain) {
      this.centralServerService.getTenantLogoBySubdomain(subdomain).subscribe({
        next: (tenantLogo: string) => {
          if (tenantLogo) {
            this.tenantLogo = tenantLogo;
          }
        },
        error: () => { /* Keep brand text fallback */ },
      });
    }
  }

  private promptForInvoice(): void {
    this.dialogService.createAndShowYesNoDialog(
      this.translateService.instant('Charging.invoice_prompt_title'),
      this.translateService.instant('Charging.invoice_prompt_message')
    ).subscribe((response) => {
      if (response === ButtonAction.YES) {
        this.messageService.showInfoMessage(
          this.translateService.instant('Charging.unplug_vehicle'),
          this.translateService.instant('Charging.loading_invoice')
        );
        this.pollForInvoice(this.userId, this.transactionDetails.id);
      } else {
        this.redirectToWattzhub();
      }
    });
  }

  private async loadChargingStation(entityId: string, connectorId: number): Promise<void> {
    this.isLoading = true;
    return new Promise<void>((resolve, reject) => {
      this.centralServerService.getChargingStation(entityId).subscribe({
        next: (chargingStation: ChargingStation) => {
          this.chargingStation = chargingStation;
          this.connectorExists = chargingStation.connectors.some((connector) => connector.connectorId === connectorId);
          this.connectorId = connectorId;
          this.connector = Utils.getConnectorFromID(chargingStation, connectorId);
          if (this.connector) {
            const statusKey = `connectorStatus.${this.connector.status.toUpperCase()}`;
            this.translateService.get(statusKey).subscribe((translatedStatus: string) => {
              this.connectorStatus = translatedStatus || this.connector.status;
              this.connectorExists = true;
              this.spinnerService.hide();
              resolve();
            });
          } else {
            this.connectorExists = false;
            this.connectorStatus = '';
            this.spinnerService.hide();
            resolve();
          }
        },
        error: (error) => {
          console.error('Failed to load charging station:', error);
          this.spinnerService.hide();
          this.connectorExists = false;
          this.connectorStatus = '';
          reject(error);
        },
      });
    }).finally(() => {
      this.isLoading = false;
    });
  }

  private pollForInvoice(userID: string, transactionID: number): void {
    let retries = 0;
    const maxRetries = 15;
    const pollInterval = 5000;
    this.isLoadingInvoice = true;
    const invoicePolling = setInterval(() => {
      if (retries >= maxRetries) {
        clearInterval(invoicePolling);
        this.isLoadingInvoice = false;
        console.error('Max retries reached. No invoice found.');
        this.spinnerService.hide();
        this.logout();
        return;
      }
      void this.getInvoiceByTransactionID(userID, transactionID).then((invoiceFound) => {
        if (invoiceFound) {
          clearInterval(invoicePolling);
          this.isLoadingInvoice = false;
          this.spinnerService.hide();
          this.logout();
        }
      });
      retries++;
    }, pollInterval);
  }

  private pollForSessionInvoice(): void {
    let retries = 0;
    const maxRetries = 15;
    const invoicePolling = setInterval(() => {
      if (retries++ >= maxRetries) {
        clearInterval(invoicePolling);
        this.isLoadingInvoice = false;
        return;
      }
      this.centralServerService.getTransactionQrSession(this.sessionToken).subscribe({
        next: (transaction) => {
          this.setSessionTransactionDetails(transaction);
          if (this.invoiceUrl) {
            clearInterval(invoicePolling);
          }
        },
        error: () => {
          clearInterval(invoicePolling);
          this.isLoadingInvoice = false;
        }
      });
    }, 5000);
  }

  private sendInvoiceToUser(invoice: any): void {
    this.spinnerService.show();
    this.isLoadingInvoice = true;
    this.centralServerService.getInvoice(invoice.id).subscribe({
      next: (invoiceBlob) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const invoiceData = JSON.parse(reader.result as string);
            this.invoiceUrl = invoiceData.downloadUrl;
          } catch (error) {
            console.error('Failed to read invoice download URL:', error);
          } finally {
            this.isLoadingInvoice = false;
            this.spinnerService.hide();
          }
        };
        reader.onerror = (error) => {
          console.error('Error reading invoice:', error);
          this.isLoadingInvoice = false;
          this.spinnerService.hide();
        };
        reader.readAsText(invoiceBlob);
      },
      error: (error) => {
        console.error('Error fetching invoice:', error);
        this.isLoadingInvoice = false;
        this.spinnerService.hide();
      }
    });
  }
}
